import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, Loader2, AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/apiClient";
import { getErrorMessage } from "@/lib/api";

const SECURITY_LABELS: Record<string, string> = {
  basic: "Basic",
  protected: "Protected",
  verified: "Verified",
};

export default function CipherDetail() {
  const params = useParams<{ code?: string }>();
  const code = params.code ?? "";

  const cipherQuery = useQuery({
    queryKey: ["cipher", code],
    queryFn: () => apiClient.getCipher(code),
    enabled: code.length > 0,
  });

  if (cipherQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading cipher…
      </div>
    );
  }

  if (cipherQuery.isError || !cipherQuery.data) {
    return (
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="h-5 w-5" />
            Cipher unavailable
          </CardTitle>
          <CardDescription>
            {cipherQuery.error
              ? getErrorMessage(cipherQuery.error)
              : "We couldn't load this cipher. It may have been removed or you may not have access."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/dashboard">
            <Button variant="outline">Back to dashboard</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const cipher = cipherQuery.data;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{cipher.name}</h1>
          <p className="text-sm text-muted-foreground font-mono mt-1">
            {cipher.cipher_code}
          </p>
        </div>
        <Link to={`/ciphers/${cipher.cipher_code}/verifications`}>
          <Button>
            <ShieldCheck className="h-4 w-4 mr-2" />
            Manage verifications
          </Button>
        </Link>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cipher details</CardTitle>
          <CardDescription>
            Read-only view. Phase 06 wires inline editing, sharing, and the
            EmCrypter encode/decode workflow into this page.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">Security level</div>
            <Badge variant="secondary" className="mt-1">
              {SECURITY_LABELS[cipher.security_level] ?? cipher.security_level}
            </Badge>
          </div>
          <div>
            <div className="text-muted-foreground">Status</div>
            <Badge variant="outline" className="mt-1">
              {cipher.status}
            </Badge>
          </div>
          <div>
            <div className="text-muted-foreground">Mapping version</div>
            <div className="font-medium mt-1">{cipher.mapping_version}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Character count</div>
            <div className="font-medium mt-1">{cipher.character_count}</div>
          </div>
          <div>
            <div className="text-muted-foreground">Passcode</div>
            <div className="font-medium mt-1">
              {cipher.has_passcode ? "Required" : "Not required"}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground">Visibility</div>
            <div className="font-medium mt-1">
              {cipher.is_public ? "Public" : "Private"}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
